import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import InquiryDialog from "@/components/InquiryDialog";
import CTAButtons from "@/components/CTAButtons";
import { Button } from "@/components/ui/button";
import {
  Route, Plane, Building2, Building, Heart, Mountain, Users, Landmark, Sparkles,
  Car, Bus, MapPin, Compass, ShieldCheck, Clock3, Star, ArrowUpRight, Check,
} from "lucide-react";

const ICONS = {
  route: Route, plane: Plane, "building-2": Building2, building: Building,
  heart: Heart, mountain: Mountain, users: Users, landmark: Landmark, sparkles: Sparkles,
  car: Car, bus: Bus, "map-pin": MapPin, compass: Compass, "shield-check": ShieldCheck,
  "clock-3": Clock3, star: Star,
};

function ServiceCard({ svc, index }) {
  const Icon = ICONS[svc.icon] || Sparkles;
  const message = `Hello! I'd like to know more about your "${svc.title}" service.`;
  return (
    <article
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
      data-testid={`service-card-${svc.slug}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={svc.image_url}
          alt={svc.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/95 backdrop-blur grid place-items-center shadow-md">
          <Icon className="w-5 h-5 text-amber-600" />
        </div>
      </div>

      <div className="p-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-600">{svc.tagline}</div>
        <h3 className="mt-2 font-display text-2xl text-slate-950 group-hover:text-amber-700 transition-colors">
          {svc.title}
        </h3>
        <p className="mt-3 text-slate-600 leading-relaxed text-sm">{svc.body}</p>

        {svc.features?.length > 0 && (
          <ul className="mt-5 grid grid-cols-2 gap-y-2 gap-x-3">
            {svc.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-5">
          <InquiryDialog
            defaultMessage={message}
            sourceRef={svc.slug}
            source="services_page"
            triggerTestId={`service-book-${svc.slug}`}
          >
            <Button
              className="rounded-full bg-slate-950 hover:bg-slate-800 text-white text-sm px-5"
              data-testid={`service-book-${svc.slug}`}
            >
              Book Now <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </InquiryDialog>
          <Link
            to="/contact"
            className="text-xs uppercase tracking-[0.24em] text-slate-500 hover:text-slate-900"
            data-testid={`service-learn-${svc.slug}`}
          >
            Talk to us →
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  const { data: services = [], isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.get("/services", { params: { is_active: true, limit: 60 } }).then((r) => r.data.items),
  });

  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy-900 text-white pt-40 pb-24">
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.32em] text-amber-400 mb-6">What we offer</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none">
              Services, quietly<br />engineered.
            </h1>
            <p className="mt-8 text-slate-300 max-w-2xl text-lg leading-relaxed">
              From private tours to airport transfers and wedding fleets — every service is built around a single idea:
              guests should feel they&apos;re travelling with a friend who happens to know the country.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <div className="border border-white/15 rounded-2xl p-6 max-w-sm">
              <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-2">Speak to a curator</div>
              <div className="font-display text-2xl leading-tight">Not sure which service fits?</div>
              <p className="text-slate-400 text-sm mt-2">Tell us your dates and we&apos;ll propose the right mix within 24 hours.</p>
              <div className="mt-5"><CTAButtons label="the right service" sourceRef="services-hero" dark testIdPrefix="services-hero" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24" data-testid="services-grid">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">Signature services</div>
            <h2 className="font-display text-4xl sm:text-5xl text-slate-950 leading-tight">Pick a starting point.</h2>
          </div>
          <p className="text-slate-500 max-w-md">Every service is customisable — combine, extend or scale to your group.</p>
        </div>

        {isLoading ? (
          <div className="text-slate-400 py-16 text-center">Loading services…</div>
        ) : services.length === 0 ? (
          <div className="text-slate-400 py-16 text-center">No services published yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => <ServiceCard svc={svc} key={svc.id} index={i} />)}
          </div>
        )}
      </section>

      {/* Process strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-28">
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">How it works</div>
        <h2 className="font-display text-4xl sm:text-5xl text-slate-950 leading-tight">Four unhurried steps.</h2>
        <div className="mt-12 grid md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Share a brief", b: "Tell us your dates, travellers and what you'd like to feel." },
            { n: "02", t: "Receive a proposal", b: "A curator drafts a tailored plan within 24 hours." },
            { n: "03", t: "Refine together", b: "Small edits, big impact — we iterate until it feels right." },
            { n: "04", t: "Travel effortlessly", b: "We handle logistics; you stay present." },
          ].map((s) => (
            <div key={s.n} className="relative pt-8 border-t-2 border-slate-950">
              <div className="font-display text-4xl text-amber-500 leading-none">{s.n}</div>
              <div className="mt-3 font-display text-xl text-slate-950">{s.t}</div>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Big CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-28">
        <div className="relative rounded-3xl overflow-hidden bg-brand-navy-900 text-white p-10 md:p-16">
          <div className="grain" />
          <div className="grid md:grid-cols-2 gap-10 items-center relative">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-4">Ready when you are</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">Book your service today.</h2>
              <p className="mt-4 text-slate-300 max-w-md">Prefer WhatsApp? Tap the button. Prefer a call? We&apos;re listening. Prefer email? Send us the brief.</p>
            </div>
            <div className="flex md:justify-end">
              <CTAButtons label="your preferred service" sourceRef="services-cta" dark testIdPrefix="services" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
